import { ConfigurationText } from '@reliance/types/models/ConfigurationText';
import { ConfigurationTextFilters } from '@reliance/types/filters/ConfigurationTextFilters';
import { useAsync, type UseAsyncReturn } from 'react-async-hook';
import api from '../common/api';

type RawConfigurationText = {
  id?: number;
  key?: string;
  content?: string;
  language?: string;
  facilityId?: number | null;
  facility?: number | { id?: number } | null;
};

function normalizeRow(raw: RawConfigurationText): ConfigurationText | null {
  if (raw.id == null || raw.key == null || raw.content == null || raw.language == null) {
    return null;
  }
  let facilityId = raw.facilityId ?? null;
  if (facilityId == null && raw.facility != null) {
    facilityId = typeof raw.facility === 'number' ? raw.facility : raw.facility.id ?? null;
  }
  return {
    id: raw.id,
    key: raw.key,
    content: raw.content,
    language: raw.language,
    facilityId: facilityId ?? undefined,
  };
}

function normalizeList(body: unknown): ConfigurationText[] {
  if (body == null) {
    return [];
  }
  const list = Array.isArray(body) ? body : [];
  return list.map((item) => normalizeRow(item as RawConfigurationText)).filter((r): r is ConfigurationText => r != null);
}

export function useConfigurationTexts(
  facilityId: number | undefined,
  language: string | undefined
): UseAsyncReturn<ConfigurationText[], [number | undefined, string | undefined]> {
  return useAsync<ConfigurationText[], [number | undefined, string | undefined]>(
    async () => {
      if (facilityId == null) {
        return [];
      }
      const params: ConfigurationTextFilters = {
        facilityId,
        ...(language != null && language !== '' ? { language } : {}),
      };
      const { data } = await api.get<unknown>('/configuration-text', { params });
      return normalizeList(data);
    },
    [facilityId, language]
  );
}
